/* eslint-disable sonarjs/cognitive-complexity -- allow */
/* eslint-disable unicorn/consistent-function-scoping -- allow */
/* eslint-disable consistent-return -- allow */
/* eslint-disable no-undefined -- allow*/
import { type ObjectLiteral } from '@repo/types';
import {
  type CreateEnv,
  type EnvOptions,
  type DefaultCombinedSchema,
  type StandardSchemaDictionary,
  type StandardSchemaV1,
  parseWithDictionary,
  ensureSynchronous,
} from '@t3-oss/env-core';

type TPrefixFormat = string | undefined;
type TServerFormat = StandardSchemaDictionary;
type TClientFormat = StandardSchemaDictionary;
type TSharedFormat = StandardSchemaDictionary;
type TExtendsFormat = Array<Record<string, unknown>>;

export const createEnv = <
  TPrefix extends TPrefixFormat,
  TServer extends TServerFormat = NonNullable<unknown>,
  TClient extends TClientFormat = NonNullable<unknown>,
  TShared extends TSharedFormat = NonNullable<unknown>,
  const TExtends extends TExtendsFormat = [],
  TFinalSchema extends StandardSchemaV1<ObjectLiteral, ObjectLiteral> = DefaultCombinedSchema<
    TServer,
    TClient,
    TShared
  >,
>(
  opts: Partial<EnvOptions<TPrefix, TServer, TClient, TShared, TExtends, TFinalSchema>>,
): CreateEnv<TFinalSchema, TExtends> => {
  const runtimeEnv = opts.runtimeEnvStrict ?? opts.runtimeEnv ?? process.env;

  const emptyStringAsUndefined = opts.emptyStringAsUndefined ?? false;

  if (emptyStringAsUndefined) {
    for (const [key, value] of Object.entries(runtimeEnv)) {
      if (value === '') {
        runtimeEnv[key] = undefined;
      }
    }
  }

  const skip = Boolean(opts.skipValidation);

  if (skip) return runtimeEnv as unknown as CreateEnv<TFinalSchema, TExtends>;

  const _client = typeof opts.client === 'object' ? opts.client : {};
  const _server = typeof opts.server === 'object' ? opts.server : {};
  const _shared = typeof opts.shared === 'object' ? opts.shared : {};
  const isServer = opts.isServer ?? !('window' in globalThis);

  const finalSchemaShape = isServer
    ? {
        ..._server,
        ..._shared,
        ..._client,
      }
    : {
        ..._client,
        ..._shared,
      };

  const parsed =
    opts.createFinalSchema?.(finalSchemaShape as never, isServer)['~standard'].validate(runtimeEnv) ??
    parseWithDictionary(finalSchemaShape, runtimeEnv);

  ensureSynchronous(parsed, 'Validation must be synchronous');

  const onValidationError =
    opts.onValidationError ??
    ((issues) => {
      console.error('❌ Invalid environment variables:', issues);
      throw new Error('Invalid environment variables');
    });

  const onInvalidAccess =
    opts.onInvalidAccess ??
    (() => {
      throw new Error('❌ Attempted to access a server-side environment variable on the client');
    });

  if (parsed.issues) {
    return onValidationError(parsed.issues);
  }

  const extendedObj = (opts.extends ?? []).reduce((acc, curr) => {
    return Object.assign(acc, curr);
  }, {});

  const fullObj = Object.assign(extendedObj, parsed.value);

  const hasEnvAccess = (prop: string) => {
    const isClientEnv = prop.startsWith(opts.clientPrefix ?? '');
    const isInObj = prop in fullObj;

    return isServer || !opts.clientPrefix || isClientEnv || isInObj;
  };

  const ignoreProp = (prop: string) => {
    return prop === '__esModule' || prop === '$$typeof';
  };

  const env = new Proxy(fullObj, {
    get(target, prop) {
      if (typeof prop !== 'string') return;
      if (ignoreProp(prop)) return;
      if (!hasEnvAccess(prop)) return onInvalidAccess(prop);

      return Reflect.get(target, prop);
    },
  });

  return env as unknown as CreateEnv<TFinalSchema, TExtends>;
};
